import React from 'react';
import NProgress from 'nprogress';
import dayjs from 'dayjs';
import { Button, Card, formatCurrency, Input, Select } from '@avila-tek/ui/src';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import { Benefit, User, ShopCart, Subscription, Plan } from '../../models';
import { FormHeader } from '../shared/Forms';
import { useNotify, useShopCart } from '../../hooks';
import { ADD_ITEM_TO_SHOP_CART } from '../../graphql/mutation';

interface AssignBenefitToEmployeeProps {
  benefits: Benefit[];
  user: User;
}

function AssignBenefitToEmployee({
  benefits,
  user,
}: AssignBenefitToEmployeeProps) {
  const router = useRouter();
  const notify = useNotify();
  const [shopCart, setShopCart] = useShopCart();
  const [disabled, setDisabled] = React.useState(false);

  const [addItemToShopCartMutation] = useMutation<{
    addItemToShopCart: ShopCart;
  }>(ADD_ITEM_TO_SHOP_CART);

  const [formState, setFormState] = React.useState({
    benefitId: '',
    date: '',
    quantity: 0,
    value: 0,
  });

  const handleOnChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    e.preventDefault();
    const { name, value } = e.target;

    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    if (name === 'benefitId') {
      setFormState((prevState) => ({
        ...prevState,
        value: benefits.find((b) => b._id === value)?.value,
      }));
    }
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      if (disabled) return;
      NProgress.start();
      setDisabled(true);

      if (
        formState.date === '' ||
        dayjs(formState.date).isBefore(dayjs().add(-1, 'day'))
      ) {
        return notify('Debe seleccionar una fecha valida', 'error');
      }

      if (formState.benefitId === null) {
        return notify('Debe seleccionar un beneficio', 'error');
      }

      if (formState.quantity <= 0) {
        return notify('Debe seleccionar una cantidad', 'error');
      }

      const shopList = [];
      const baseShopItem = {
        client: (user as User)?.client._id,
        quantity: Number(formState.quantity),
        availableDate: dayjs(formState.date).toDate(),
        benefitPrice: null,
      };

      // if user has discount coupons
      if (formState.quantity <= user.discountCoupons) {
        shopList.push({
          ...baseShopItem,
          benefitPrice: (
            (user.currentSubscription as Subscription).plan as Plan
          ).couponPrice,
        });
        // if user wants more quantity than discount coupons
      } else if (formState.quantity > user.discountCoupons) {
        // add discount coupons to shop list
        if (user.discountCoupons > 0) {
          shopList.push({
            ...baseShopItem,
            quantity: user.discountCoupons,
            benefitPrice: (
              (user.currentSubscription as Subscription).plan as Plan
            ).couponPrice,
          });
        }
        // add the rest of the quantity to shop list
        shopList.push({
          ...baseShopItem,
          quantity: formState.quantity - user.discountCoupons,
          benefitPrice: 5,
        });
      }

      const { data } = await addItemToShopCartMutation({
        variables: {
          data: {
            shopCart: shopCart._id,
            items: {
              benefit: formState.benefitId,
              shopList,
            },
          },
        },
      });
      if (data?.addItemToShopCart?._id) {
        notify('Se ha agreagado al carrito con éxito', 'success');
        setShopCart(data?.addItemToShopCart);
      } else {
        notify('Ha ocurrido un error al intentar agregar al carrito', 'error');
      }
      router.push('/app/shopcart');
    } catch (error) {
      console.log(error);
      notify(error.message, 'error');
    } finally {
      setDisabled(false);
      NProgress.done();
    }
  };
  return (
    <form onSubmit={onSubmit}>
      <Card>
        <FormHeader title="Asignar beneficio" />
        <Card.Body>
          <div className="w-full flex md:flex-row flex-col gap-5">
            <div className="w-full">
              <Select
                value={formState.benefitId}
                label="Beneficio"
                onChange={handleOnChange}
                name="benefitId"
                required
              >
                <option value="" disabled>
                  Seleccione un beneficio
                </option>
                {benefits.map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.title}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-full">
              <Input
                value={`$${formatCurrency(formState.value)}`}
                label="Importe"
                type="text"
                disabled
              />
            </div>
          </div>
          <div className="w-full flex md:flex-row flex-col gap-5">
            <div className="w-full">
              <Input
                value={formState.quantity}
                label="Cantidad"
                type="number"
                name="quantity"
                onChange={handleOnChange}
                required
              />
              {user?.discountCoupons > 0 ? (
                <span className="text-sm text-purple-300 font-semibold">
                  El usuario dispone de {user.discountCoupons} a precio
                  preferencial: $
                  {formatCurrency(
                    ((user.currentSubscription as Subscription).plan as Plan)
                      .couponPrice
                  )}
                  . Si agrega mayor a la cantidad los beneficios tendran un
                  costo $5.00
                </span>
              ) : null}
            </div>
            <div className="w-full">
              <Input
                value={formState.date}
                label="Fecha de entrega"
                type="date"
                name="date"
                placeholder="Seleccione la fecha de entrega"
                onChange={handleOnChange}
              />
            </div>
          </div>
        </Card.Body>
      </Card>
      <div className="flex justify-end mt-5">
        <div>
          <Button type="submit">Agregar al carrito</Button>
        </div>
      </div>
    </form>
  );
}

export default AssignBenefitToEmployee;
